import { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Col,
  Row,
  DataGridView,
  NMWaitIndicator,
} from "@neumetric/blueprint-lib/components";
import { auditActions, getAuditUploadHistory } from "../../store";

const ImportStatusColumns = [
  {
    dataIndex: "file_name",
    typeRepresentation: "TEXT",
    title: "Filename",
    width: "24%",
    searchableFlag: true,
    sortableFlag: true,
    searchMode: "CLIENT",
  },
  {
    dataIndex: "uploaded_at",
    typeRepresentation: "DATE",
    title: "Upload Date",
    width: "18%",
    sortableFlag: true,
    semantics: {
      format: "ddd, DD-MMM-YYYY",
    },
  },
  {
    dataIndex: "success_count",
    typeRepresentation: "TEXT",
    title: "Control Updated",
    width: "20%",
    searchable: true,
    sortable: true,
  },
  {
    dataIndex: "failed_count",
    typeRepresentation: "TEXT",
    title: "Control Updated failed",
    width: "26%",
    searchable: true,
    sortable: true,
  },
  {
    dataIndex: "download",
    typeRepresentation: "ACTION",
    title: "Download",
    width: "12%",
    classes: {
      content: "justify-center",
    },
  },
];

export const AuditImportStatus = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { asmtId } = useParams();
  const [downloadings, setDownloadings] = useState(new Set());
  const auditUploadHistory = useSelector(getAuditUploadHistory);
  useEffect(() => {
    if (asmtId) {
      dispatch(auditActions.fetchAuditUploadHistory({ auditId: asmtId }));
    }
  }, [asmtId]);

  const lastHistory =
    Array.isArray(auditUploadHistory) && auditUploadHistory.length > 0
      ? auditUploadHistory[0]
      : null;

  const handleDownloadHistory = useCallback(
    (record) => {
      if (!record.id || downloadings.has(record.id)) {
        return;
      }
      setDownloadings((prev) => new Set(prev).add(record.id));
      const action = auditActions.downloadUploadHistory({
        id: record.id,
        filename: record.file_name,
      });
      action.onComplete = () => {
        setDownloadings((prev) => {
          const newSet = new Set(prev);
          newSet.delete(record.id);
          return newSet;
        });
      };
      dispatch(action);
    },
    [downloadings, asmtId],
  );

  const getCellSemantics = useCallback(
    (column, record) => {
      const semantics = {};
      if (column.dataIndex === "download") {
        const loading = record.id && downloadings.has(record.id);
        semantics.actions = [
          {
            icon: "DOWNLOAD",
            loading,
            onClick: handleDownloadHistory.bind(null, record),
          },
        ];
      }
      return semantics;
    },
    [handleDownloadHistory, downloadings],
  );
  return (
    <Col className="w-100 h-100 overflow-hide pl-20 pr-20 audit-upload-history">
      <Link className="mt-4 mb-12" to={"#"} onClick={() => navigate(-1)}>
        <span className="fs-14 fw-400 text-action">Back</span>
      </Link>
      <Col className="fs-20 fw-600 line-auto text-heading">Import Status</Col>
      <Row className="w-100 mt-20 pr-25" justify="between">
        <Col className="w-47">
          <Col className="heading w-100 pt-12 pb-12 pl-20 mb-10">
            <span className="fs-18 fw-600 text-filled">Upload Result</span>
          </Col>
          <Col className="w-100">
            {auditUploadHistory === null ? (
              <Col className="w-100 h-250-px" align="center" justify="center">
                <NMWaitIndicator size="small" />
              </Col>
            ) : Array.isArray(auditUploadHistory) &&
              auditUploadHistory.length === 0 ? (
              <Col className="w-100 h-250-px" align="center" justify="center">
                <span className="fs-14 fw-400 text-subtitle">
                  No upload result
                </span>
              </Col>
            ) : (
              <Col className="w-100 fs-16 fw-400 text-filled pl-20">
                <span className="mb-32">
                  <span className="fw-600 mr-4">
                    {lastHistory.base_file_name}
                  </span>
                  was successfully uploaded with the following results…
                </span>
                <span>
                  Controls Updated:
                  <span className="fw-600 ml-4 mr-4">
                    {lastHistory.success_count}
                  </span>
                </span>
                <span>
                  Controls Update <span className="text-caution ">failed</span>:
                  <span className="fw-600 ml-4">
                    {lastHistory.failed_count}
                  </span>
                </span>
                {Number(lastHistory.failed_count) > 0 && (
                  <>
                    <span>
                      Audit upload errors:
                      <span className="text-action ml-4">
                        {lastHistory.file_name}
                      </span>
                    </span>
                    <span>Fix the errors & re-upload them please!</span>
                  </>
                )}
              </Col>
            )}
          </Col>
        </Col>
        <Col className="w-52">
          <Col className="heading w-100 pt-12 pb-12 pl-20 mb-10">
            <span className="fs-18 fw-600 text-filled">Upload History</span>
          </Col>
          <Col className="w-100 h-250-px">
            <DataGridView
              getCellSemantics={getCellSemantics}
              columns={ImportStatusColumns}
              dataSource={auditUploadHistory}
              EmptyComponent="No upload history"
            />
          </Col>
        </Col>
      </Row>
    </Col>
  );
};
